"use client";

import { useMemo, useState } from "react";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { writeToClipboard } from "@/lib/clipboard";
import { t, tPlural } from "@/lib/i18n/interpolate";
import { stripMarkdown } from "@/lib/tool-kit/developer/markdown-strip";

/* ------------------------------------------------------------------ */
/*  Stat card                                                         */
/* ------------------------------------------------------------------ */

function StatCard({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="rounded-lg border border-border bg-card p-4 text-center">
      <p className="font-semibold text-2xl text-foreground">{value}</p>
      <p className="mt-1 text-muted-foreground text-sm">{label}</p>
    </div>
  );
}

/** English strings. A translated content file overrides these through `ui`. */
export const MARKDOWN_TO_TEXT_STRINGS = {
  pasteYourMarkdown: "Paste your Markdown",
  inputPlaceholder: "Paste Markdown here, e.g. # Hello **world**...",
  inputCharacters: "Input Characters",
  outputCharacters: "Output Characters",
  elementsStripped: "Elements Stripped",
  plainTextOutput: "Plain Text Output",
  copied: "Copied!",
  copyOutput: "Copy Output",
  noTextContentFound: "No text content found after stripping Markdown.",
  comparison: "Comparison",
  input: "Input",
  output: "Output",
  /*
   * Two counts, so a frame and two plural nouns. It was `{chars}{" characters, "}
   * {lines}{" lines"}`, which rendered "1 characters, 1 lines" in English and gave a
   * translator two fragments they could not agree with anything.
   */
  charsAndLines: "{chars}, {lines}",
  characterCount: "one: {count} character|other: {count} characters",
  lineCount: "one: {count} line|other: {count} lines",
} as const;

export default function MarkdownToTextWidget() {
  const [text, setText] = useState("");
  const [copied, setCopied] = useState(false);

  const strings = useToolUi(MARKDOWN_TO_TEXT_STRINGS);

  const fmt = useToolNumber();
  const locale = useToolLocale();
  const result = useMemo(() => stripMarkdown(text), [text]);

  function handleCopy() {
    if (result) {
      writeToClipboard(result.output).then((copied) => {
        if (!copied) {
          return;
        }

        setCopied(true);
        setTimeout(() => setCopied(false), 1500);
      });
    }
  }

  return (
    <>
      {/* Input */}
      <div className="space-y-2">
        <label
          className="font-medium text-foreground text-sm"
          htmlFor="md-input"
        >
          {strings.pasteYourMarkdown}
        </label>
        <textarea
          className="min-h-[200px] w-full resize-y rounded-lg border border-border bg-card p-4 font-mono text-foreground text-sm leading-relaxed placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
          id="md-input"
          onChange={(e) => setText(e.target.value)}
          placeholder={strings.inputPlaceholder}
          value={text}
        />
      </div>

      {/* Stats */}
      {result && (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3">
          <StatCard
            label={strings.inputCharacters}
            value={fmt(result.inputChars)}
          />
          <StatCard
            label={strings.outputCharacters}
            value={fmt(result.outputChars)}
          />
          <StatCard
            label={strings.elementsStripped}
            value={fmt(result.elementsRemoved)}
          />
        </div>
      )}

      {/* Output */}
      {result && (
        <section className="space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="font-semibold text-foreground text-lg">
              {strings.plainTextOutput}
            </h2>
            <button
              className="rounded-md border border-border bg-background px-3 py-1.5 text-muted-foreground text-sm hover:bg-muted hover:text-foreground"
              onClick={handleCopy}
              type="button"
            >
              {copied ? strings.copied : strings.copyOutput}
            </button>
          </div>
          <div className="min-h-[120px] whitespace-pre-wrap rounded-lg border border-border bg-card p-4 text-foreground text-sm leading-relaxed">
            {result.output || (
              <span className="text-muted-foreground">
                {strings.noTextContentFound}
              </span>
            )}
          </div>
        </section>
      )}

      {/* Side-by-side comparison */}
      {result && (
        <section className="space-y-3">
          <h2 className="font-semibold text-foreground text-lg">
            {strings.comparison}
          </h2>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="rounded-lg border border-border bg-card p-4">
              <h3 className="mb-2 font-medium text-muted-foreground text-xs uppercase tracking-wider">
                {strings.input}
              </h3>
              <p className="text-foreground text-sm">
                {t(strings.charsAndLines, {
                  chars: tPlural(
                    strings.characterCount,
                    locale,
                    result.inputChars
                  ).trim(),
                  lines: tPlural(
                    strings.lineCount,
                    locale,
                    result.inputLines
                  ).trim(),
                })}
              </p>
            </div>
            <div className="rounded-lg border border-border bg-card p-4">
              <h3 className="mb-2 font-medium text-muted-foreground text-xs uppercase tracking-wider">
                {strings.output}
              </h3>
              <p className="text-foreground text-sm">
                {t(strings.charsAndLines, {
                  chars: tPlural(
                    strings.characterCount,
                    locale,
                    result.outputChars
                  ).trim(),
                  lines: tPlural(
                    strings.lineCount,
                    locale,
                    result.outputLines
                  ).trim(),
                })}
              </p>
            </div>
          </div>
        </section>
      )}
    </>
  );
}
